import { callEndConfirmText } from "../calls/call-row.ts";
import { CALL_ENTRY_CHAT_EMPTY } from "../schedule/entry-chat.ts";
import type { CallRole } from "./call-exit.ts";

/**
 * Слова комнаты у звонка с преподавателями (6.4, 9.2 и 9.5
 * `docs/teacher-calls-design.md`). Чистые функции без React, чтобы проверять их
 * встроенным `node:test`.
 *
 * **Правило одно (решение главной сессии):** у звонка слова «урок» и «занятие»
 * остаются («Кто на занятии», «Завершить занятие»), а меняются только тексты,
 * где названы «ученик», «ученики» или «архив». Ученика в звонке нет, архива у
 * звонка нет, и ведущий читал бы про людей и место, которых не существует.
 * Вместо ученика говорится «участник»: тем же словом звонок называет людей в
 * кабинете («ответов участников», строка о видимости).
 *
 * ⚠️ **Слово «гость» на экран не выходит нигде** (решение главной сессии): это
 * наш словарь. Того, кто прислал ссылку, тексты называют «тот, кто прислал
 * ссылку», и в комнате его не называют вовсе.
 *
 * ⚠️ **Звонок спрашивается раньше пары.** Звонок для комнаты это пара 1:1 с
 * несколькими собеседниками роли `student` (6.4 проекта), то есть признак
 * `pair` у него истинен, и правило, ветвящееся по одной паре, написало бы
 * «ученику». Ровно эту поломку ловит `call-words.test.ts`.
 *
 * ⚠️ Модуль берут и стартовая страница урока (через `call-exit.ts`), и задания
 * на доске, поэтому в нём нет ничего тяжелее строк: только кабинетные тексты
 * звонка (`features/calls/call-row.ts`) и пустая лента чата
 * (`features/schedule/entry-chat.ts`), обе уже лежат в стартовом чанке.
 */

/** Кому адресован рычаг или кнопка: ученику пары, ученикам группы, участникам звонка. */
export type Audience = "pair" | "group" | "call";

export function audienceOf(input: { pair: boolean; call: boolean }): Audience {
  if (input.call) return "call";
  return input.pair ? "pair" : "group";
}

/** Кому: «ученику», «ученикам», «участникам». */
export function toAudience(a: Audience): string {
  if (a === "call") return "участникам";
  return a === "pair" ? "ученику" : "ученикам";
}

/** Кто видит: «Ученик видит», «Ученики видят», «Участники видят». */
export function whoSeesText(a: Audience): string {
  if (a === "call") return "Участники видят";
  return a === "pair" ? "Ученик видит" : "Ученики видят";
}

/* ─────────────────────────── конец звонка ────────────────────────────── */

/**
 * Экраны конца (9.5 проекта). Везде «завершён», а не «закончился»: то же слово
 * стоит в итоговых текстах кабинета («Ссылка не работает. Возможно, звонок
 * завершён…», «Звонок завершится для всех…»).
 */
export const CALL_ENDED_TEXT = "Звонок завершён";
export const CALL_REMOVED_TEXT = "Вас убрали из звонка";
/**
 * Причину знать неоткуда: карточка звонка отвечает 404 и после конца, и после
 * удаления (раздел 4.4 проекта). Названы обе, а не одна наугад.
 */
export const CALL_GONE_TEXT = "Звонок завершён или вас из него убрали";
/** Кнопка экрана конца. «Уроки» это раздел, где звонки и живут. */
export const CALL_BACK_LABEL = "К урокам";

/**
 * Значок доски у звонка: и отказ в доступе, и конец это одно и то же «вас тут
 * больше нет». Строчной буквой, как остальные состояния значка.
 *
 * ⚠️ **Слова по роли** (круг 1 ревью). Конец звонка закрывает сокеты доски и у
 * ведущего (вторая вкладка, ветка без видео), тикет отвечает 404, и «вас из
 * него убрали» ведущему было бы неправдой: его не убирают никогда.
 */
export const CALL_BOARD_GONE = "звонок завершён или вас из него убрали";
export const CALL_BOARD_ENDED = "звонок завершён";

export function boardGoneText(role: CallRole): string {
  return role === "host" ? CALL_BOARD_ENDED : CALL_BOARD_GONE;
}

/** Подтверждение «Завершить» в комнате (9.2 проекта). */
export const LESSON_END_CONFIRM =
  "Занятие закончится, и вернуться в комнату будет нельзя. Доска и ответы останутся в архиве.";

/**
 * У звонка текст тот же, что у «В корзину» в строке «Уроков», и берётся
 * оттуда, а не пишется заново: два предписывающих места разошлись бы при первой
 * правке срока.
 */
export function endConfirmText(call: boolean): string {
  return call ? callEndConfirmText() : LESSON_END_CONFIRM;
}

/* ─────────────────────── ссылка для преподавателей ────────────────────── */

/** Блок в списке участников вместо ссылки ученику (9.2 проекта). */
export const GUEST_LINK_TITLE = "Ссылка для преподавателей";
export const GUEST_LINK_COPY = "Скопировать";
export const GUEST_LINK_COPIED = "Скопировано";
export const GUEST_LINK_REVOKE = "Отозвать";
export const GUEST_LINK_NEW = "Новая ссылка";
/** Отзыв не выбивает вошедших (П4 проекта), и текст говорит именно это. */
export const GUEST_LINK_REVOKED = "Ссылка отозвана. Кто уже в звонке, тот останется.";
export const GUEST_LINK_REVOKE_FAILED = "Не удалось отозвать ссылку. Попробуйте ещё раз.";

/* ───────────────────────── убрать из звонка ──────────────────────────── */

export const REMOVE_LABEL = "Убрать из звонка";
export const REMOVE_YES = "Да, убрать";
export const REMOVE_FAILED = "Не удалось убрать из звонка. Попробуйте ещё раз.";

/**
 * Подтверждение «убрать». Последствие названо до нажатия и названо целиком:
 * вернуть убранного в этот звонок нельзя никакой ссылкой (П5 проекта).
 *
 * Пустая подпись (плитка без имени) не даёт фразы без подлежащего.
 */
export function removeConfirmText(name: string): string {
  const who = name.trim() === "" ? "Участник" : name.trim();
  return `${who} выйдет из звонка и не сможет вернуться.`;
}

/** Имя кнопки для читалки: у строки и у плитки рядом стоят чужие кнопки. */
export function removeLabelFor(name: string): string {
  return name.trim() === "" ? REMOVE_LABEL : `${REMOVE_LABEL}: ${name.trim()}`;
}

/* ──────────────────────── тексты комнаты (6.4) ────────────────────────── */

/** Подсказка кнопки микрофона на чужой плитке и в строке списка. */
export function micHint(allowed: boolean, call: boolean): string {
  const whom = call ? "участнику" : "ученику";
  return allowed
    ? `Выключить микрофон ${whom}. Вернуть его сможете только вы`
    : `Вернуть микрофон ${whom}. Включит он сам`;
}

/** Что сделает кнопка пера. */
export function penActionText(on: boolean, a: Audience): string {
  return on ? `Закрыть доску ${toAudience(a)}` : `Открыть доску ${toAudience(a)}`;
}

/** Состав не приехал в пункте «Что видно всем». */
export function rosterNamesFailedText(call: boolean): string {
  return `Имена ${call ? "участников" : "учеников"} не загрузились. Пока показать можно только себя.`;
}

/**
 * Строка для читалки у «Что видно всем», когда выбран план «друг друга», а
 * собеседника на площади нет.
 */
export function mutualGoneText(call: boolean): string {
  return call ? "Участника не видно, на экране доска" : "Ученика не видно, на экране доска";
}

/** Та же строка, когда на площади чужая камера, а имени нет. */
export function cameraOfText(call: boolean): string {
  return call ? "Показываете камеру участника" : "Показываете камеру ученика";
}

/**
 * Пустая лента чата в комнате звонка. Тот же текст, что у строки звонка в
 * «Уроках»: «до и после» здесь нет, после конца звонка чата нет вовсе.
 */
export const CALL_ROOM_CHAT_EMPTY = CALL_ENTRY_CHAT_EMPTY;

/** Слова кнопки «Показать ответ» у задания на доске. */
export interface RevealWords {
  /** Ответ раскрыт. */
  shown: string;
  /** Шаг подтверждения. */
  ask: string;
  /** Подсказка у кнопки. */
  title: string;
  /** Раскрыть не удалось. */
  failed: string;
}

export function revealWords(call: boolean): RevealWords {
  return call
    ? {
        shown: "ответ показан участникам",
        ask: "участники увидят правильный ответ, отменить нельзя",
        title: "Участники увидят правильный ответ. Отменить нельзя",
        failed: "не удалось, участники ответа не видят",
      }
    : {
        shown: "ответ показан ученику",
        ask: "ученик увидит правильный ответ, отменить нельзя",
        title: "Ученик увидит правильный ответ. Отменить нельзя",
        failed: "не удалось, ученик ответа не видит",
      };
}

/**
 * Подсказка «Сбросить ответ». У звонка ответ общий на всех (П9 проекта: ответы
 * гостей лежат в одном документе), поэтому и убирается ответ участников.
 * «Попытка это факт занятия» у звонка снята: это наш словарь (круг 1 ревью).
 */
export function resetHint(call: boolean): string {
  return call
    ? "Ответ участников будет убран. Попытки останутся."
    : "Ответ ученика будет убран. Попытки при этом останутся: попытка это факт занятия";
}
